import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ProductEditor } from "./ProductEditor";

export const ProductsManager = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=list | "__new" | slug
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/data/products"); setRows(data); } catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { if (editing === null) load(); }, [editing]);

  const del = async () => {
    try { await api.delete(`/admin/crud/products/${confirm.id}`); toast.success("Deleted"); setConfirm(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (editing !== null) return <ProductEditor slug={editing === "__new" ? null : editing} onBack={() => setEditing(null)} />;

  return (
    <div data-testid="products-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-4xl text-white">Products</h1>
        <Button onClick={() => setEditing("__new")} className="btn-primary rounded-lg" data-testid="product-add"><Plus size={15} className="mr-2" />New Product</Button>
      </div>

      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading…</div>}
      {!loading && rows.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No products yet.</div>}
      {!loading && rows.length > 0 && (
        <div className="glass rounded-2xl overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              {["name", "slug", "status", "featured"].map((c) => <th key={c} className="p-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">{c}</th>)}
              <th className="p-3 text-right text-[10px] font-mono uppercase tracking-widest text-slate-500">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`product-row-${r.slug}`}>
                  <td className="p-3 text-slate-200">{r.name}</td>
                  <td className="p-3 font-mono text-xs text-slate-400">{r.slug}</td>
                  <td className="p-3 font-mono text-xs text-slate-400">{r.status || "—"}</td>
                  <td className="p-3 font-mono text-xs text-slate-400">{r.featured ? "Yes" : "No"}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setEditing(r.slug)} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" data-testid={`product-edit-${r.slug}`}><Pencil size={14} /></button>
                      <button onClick={() => setConfirm(r)} className="p-2 rounded-lg hover:bg-rose-500/15 text-rose-400" data-testid={`product-del-${r.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="glass-strong border-white/10" data-testid="product-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{confirm?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the product. Its variants and tiers are not auto-deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-500" data-testid="product-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
